// ============================================
// SERVICES: API - Configuración centralizada
// ============================================
// Archivo: src/services/api.js

import * as Sentry from '@sentry/react';
import { cacheGet, cacheSet, cacheDel, cacheClear } from '../utils/apiCache';

const API_URL = process.env.REACT_APP_API_URL;

// Headers para requests JSON autenticadas (cookie httpOnly maneja la auth)
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Cache-Control': 'no-cache',
  'Pragma': 'no-cache'
});

// Headers sin autenticación
const publicHeaders = () => ({
  'Content-Type': 'application/json'
});

// Manejo de respuestas — normaliza { data: X } → X para controllers que usan R.success
const handleResponse = async (response) => {
  if (response.status === 401) {
    window.location.href = '/';
    throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error en el servidor' }));
    const err = new Error(error.message || `Error ${response.status}`);
    // Solo reportar 5xx a Sentry — los 4xx son errores del cliente
    if (response.status >= 500) {
      Sentry.captureException(err, { extra: { url: response.url, status: response.status } });
    }
    throw err;
  }
  const json = await response.json();
  // Unwrap { data: X } produced by R.success / R.created on the backend
  if (
    json !== null &&
    typeof json === 'object' &&
    !Array.isArray(json) &&
    Object.prototype.hasOwnProperty.call(json, 'data')
  ) {
    return json.data;
  }
  return json;
};

// ============================================
// AUTH SERVICES
// ============================================
export const authService = {
  login: async (email, password) => {
    cacheClear(); // Descartar caché de sesión anterior
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: publicHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    return handleResponse(response);
  },

  me: async () => {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('No session');
    return response.json();
  },

  changePassword: async (newPassword, currentPassword = null) => {
    const response = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        newPassword,
        ...(currentPassword && { currentPassword })
      })
    });
    return handleResponse(response);
  },

  forgotPassword: async (email) => {
    const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return handleResponse(res);
  }
};

// ============================================
// USER SERVICES
// ============================================
export const userService = {
  getAll: async () => {
    const cached = cacheGet('users:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/users`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('users:all', data, 300); // 5 min
    return data;
  },

  create: async (userData) => {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(userData)
    });
    const data = await handleResponse(response);
    cacheDel('users:all');
    return data;
  },

  update: async (id, userData) => {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(userData)
    });
    const data = await handleResponse(response);
    cacheDel('users:all');
    return data;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
      credentials: 'include'
    });
    const data = await handleResponse(response);
    cacheDel('users:all');
    return data;
  }
};

// ============================================
// COMPANY SERVICES
// ============================================
export const companyService = {
  getAll: async () => {
    const cached = cacheGet('companies:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/companies`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('companies:all', data, 3600); // 1 hora
    return data;
  }
};

// ============================================
// PLANT SERVICES
// ============================================
export const plantService = {
  getAll: async () => {
    const cached = cacheGet('plants:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/plants`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('plants:all', data, 600); // 10 min
    return data;
  },

  getById: async (id) => {
    const response = await fetch(`${API_URL}/api/plants/${id}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  create: async (plantData) => {
    const response = await fetch(`${API_URL}/api/plants`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(plantData)
    });
    const data = await handleResponse(response);
    cacheDel('plants:all');
    return data;
  },

  update: async (id, plantData) => {
    const response = await fetch(`${API_URL}/api/plants/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(plantData)
    });
    const data = await handleResponse(response);
    cacheDel('plants:all');
    return data;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/plants/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
      credentials: 'include'
    });
    const data = await handleResponse(response);
    cacheDel('plants:all');
    return data;
  }
};

// ============================================
// EQUIPMENT SERVICES
// ============================================
export const equipmentService = {
  getAll: async () => {
    const cached = cacheGet('equipments:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/equipments?limit=1000`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('equipments:all', data, 300); // 5 min
    return data;
  },

  getByPlant: async (plantId) => {
    const response = await fetch(`${API_URL}/api/equipments/plant/${plantId}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_URL}/api/equipments/${id}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  create: async (equipmentData) => {
    const response = await fetch(`${API_URL}/api/equipments`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(equipmentData)
    });
    const data = await handleResponse(response);
    cacheDel('equipments:all');
    return data;
  },

  update: async (id, equipmentData) => {
    const response = await fetch(`${API_URL}/api/equipments/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(equipmentData)
    });
    const data = await handleResponse(response);
    cacheDel('equipments:all');
    return data;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/equipments/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
      credentials: 'include'
    });
    const data = await handleResponse(response);
    cacheDel('equipments:all');
    return data;
  }
};

// ============================================
// MAINTENANCE SERVICES
// ============================================
export const maintenanceService = {
  getAll: async () => {
    const cached = cacheGet('maintenances:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/maintenances`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('maintenances:all', data, 120); // 2 min
    return data;
  },

  getByEquipment: async (equipmentId) => {
    const response = await fetch(`${API_URL}/api/maintenances/equipment/${equipmentId}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  create: async (maintenanceData) => {
    const response = await fetch(`${API_URL}/api/maintenances`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(maintenanceData)
    });
    const data = await handleResponse(response);
    cacheDel('maintenances:all');
    return data;
  },

  update: async (id, maintenanceData) => {
    const response = await fetch(`${API_URL}/api/maintenances/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(maintenanceData)
    });
    const data = await handleResponse(response);
    cacheDel('maintenances:all');
    return data;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/maintenances/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
      credentials: 'include'
    });
    const data = await handleResponse(response);
    cacheDel('maintenances:all');
    return data;
  }
};

// ============================================
// CALIBRATION SERVICES
// ============================================
export const calibrationService = {
  getAll: async () => {
    const cached = cacheGet('calibrations:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/calibrations`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('calibrations:all', data, 120); // 2 min
    return data;
  },

  getByEquipment: async (equipmentId) => {
    const response = await fetch(`${API_URL}/api/calibrations/equipment/${equipmentId}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  create: async (calibrationData) => {
    const isFormData = calibrationData instanceof FormData;
    const response = await fetch(`${API_URL}/api/calibrations`, {
      method: 'POST',
      headers: isFormData ? {} : authHeaders(),
      credentials: 'include',
      body: isFormData ? calibrationData : JSON.stringify(calibrationData)
    });
    const data = await handleResponse(response);
    cacheDel('calibrations:all');
    return data;
  },

  update: async (id, calibrationData) => {
    const isFormData = calibrationData instanceof FormData;
    const response = await fetch(`${API_URL}/api/calibrations/${id}`, {
      method: 'PUT',
      headers: isFormData ? {} : authHeaders(),
      credentials: 'include',
      body: isFormData ? calibrationData : JSON.stringify(calibrationData)
    });
    const data = await handleResponse(response);
    cacheDel('calibrations:all');
    return data;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/calibrations/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
      credentials: 'include'
    });
    const data = await handleResponse(response);
    cacheDel('calibrations:all');
    return data;
  }
};

// ============================================
// FAULT SERVICES
// ============================================
export const faultService = {
  getAll: async () => {
    const cached = cacheGet('faults:all');
    if (cached !== null) return cached;
    const response = await fetch(`${API_URL}/api/faults`, { headers: authHeaders(), credentials: 'include' });
    const data = await handleResponse(response);
    if (data && data.length > 0) cacheSet('faults:all', data, 60); // 1 min — datos operativos
    return data;
  },

  getByPlant: async (plantId) => {
    const response = await fetch(`${API_URL}/api/faults/plant/${plantId}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  getByEquipment: async (equipmentId) => {
    const response = await fetch(`${API_URL}/api/faults/equipment/${equipmentId}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  },

  create: async (faultData) => {
    const formData = new FormData();

    formData.append('plantId', faultData.plantId);
    formData.append('fechaReporte', faultData.fechaReporte);
    formData.append('horaInicio', faultData.horaInicio);
    formData.append('tipoFalla', faultData.tipoFalla);
    formData.append('descripcion', faultData.descripcion);
    formData.append('prioridad', faultData.prioridad);
    formData.append('impacto', faultData.impacto);
    formData.append('estado', faultData.estado);

    if (faultData.equipmentId) formData.append('equipmentId', faultData.equipmentId);
    if (faultData.estadoEquipo) formData.append('estadoEquipo', faultData.estadoEquipo);
    if (faultData.componenteAfectado) formData.append('componenteAfectado', faultData.componenteAfectado);
    if (faultData.equipoAfectado) formData.append('equipoAfectado', faultData.equipoAfectado);

    if (faultData.accionInmediata) formData.append('accionInmediata', faultData.accionInmediata);
    if (faultData.responsableReporte) formData.append('responsableReporte', faultData.responsableReporte);

    if (faultData.numeroOc) formData.append('numeroOc', faultData.numeroOc);
    if (faultData.fechaOc) formData.append('fechaOc', faultData.fechaOc);
    if (faultData.montoOc) formData.append('montoOc', faultData.montoOc);
    if (faultData.proveedor) formData.append('proveedor', faultData.proveedor);
    if (faultData.quienApruebaOc) formData.append('quienApruebaOc', faultData.quienApruebaOc);
    if (faultData.estadoOc) formData.append('estadoOc', faultData.estadoOc);

    if (faultData.fileEvidencia) formData.append('evidencia', faultData.fileEvidencia);
    if (faultData.fileOc) formData.append('oc', faultData.fileOc);
    if (faultData.filePresupuesto) formData.append('presupuesto', faultData.filePresupuesto);

    const response = await fetch(`${API_URL}/api/faults`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    const data = await handleResponse(response);
    cacheDel('faults:all');
    return data;
  },

  update: async (id, faultData) => {
    const { fileEvidencia, fileOc, filePresupuesto, ...fields } = faultData;
    const hasFiles = fileEvidencia || fileOc || filePresupuesto;
    let body, headers;
    if (hasFiles) {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
      if (fileEvidencia)   fd.append('evidencia',   fileEvidencia);
      if (fileOc)          fd.append('oc',           fileOc);
      if (filePresupuesto) fd.append('presupuesto',  filePresupuesto);
      body = fd;
      headers = {};
    } else {
      body = JSON.stringify(fields);
      headers = authHeaders();
    }
    const response = await fetch(`${API_URL}/api/faults/${id}`, { method: 'PUT', headers, credentials: 'include', body });
    const data = await handleResponse(response);
    cacheDel('faults:all');
    return data;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/faults/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
      credentials: 'include'
    });
    const data = await handleResponse(response);
    cacheDel('faults:all');
    return data;
  },

  export: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_URL}/api/faults/export?${params}`, { headers: authHeaders(), credentials: 'include' });
    return handleResponse(response);
  }
};

const services = {
  authService,
  userService,
  companyService,
  plantService,
  equipmentService,
  calibrationService,
  maintenanceService,
  faultService
};

export default services;
